import React, { useEffect, useState, Children, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sun, Utensils, ClipboardCheck, Check, X, ArrowRight, BookOpen, Clock, ShieldCheck, Download } from 'lucide-react';
// Meta Pixel ID
const META_PIXEL_ID = '871359405780071';
// Animation variants for scroll reveals
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 15
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
// Modal component for Privacy and Terms
function Modal({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  if (!isOpen) return null;
  return <AnimatePresence data-id="element-1">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 bg-black/50 z-50" onClick={onClose} data-id="element-2" />
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} className="fixed inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:max-w-2xl md:w-full bg-white rounded-2xl shadow-2xl z-50 overflow-hidden flex flex-col max-h-[90vh]" data-id="element-3">
        <div className="flex items-center justify-between p-4 border-b border-gray-100" data-id="element-4">
          <h2 className="text-lg font-serif font-bold text-brand-dark" data-id="element-5">
            {title}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-id="element-6">
            <X className="w-5 h-5 text-gray-500" data-id="element-7" />
          </button>
        </div>
        <div className="p-5 overflow-y-auto text-gray-700 leading-relaxed text-sm" data-id="element-8">
          {children}
        </div>
      </motion.div>
    </AnimatePresence>;
}
export function LandingPage() {
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const ebookImage = 'https://cdn.magicpatterns.com/uploads/x373564ab2EebAe9sAPJ6p/Ebook_Menopausia_en_Calma.png';
  // Initialize Meta Pixel on mount
  useEffect(() => {
    try {
      if (typeof window === 'undefined') return;
      // Create fbq function if it doesn't exist
      if (!(window as any).fbq) {
        const fbq: any = function (...args: any[]) {
          if (fbq.callMethod) {
            fbq.callMethod.apply(fbq, args);
          } else {
            fbq.queue.push(args);
          }
        };
        fbq.push = fbq;
        fbq.loaded = true;
        fbq.version = '2.0';
        fbq.queue = [];
        (window as any).fbq = fbq;
        (window as any)._fbq = fbq;
        // Load the pixel script
        const script = document.createElement('script');
        script.async = true;
        script.src = 'https://connect.facebook.net/en_US/fbevents.js';
        document.head.appendChild(script);
      }
      // Initialize and track PageView
      ;
      (window as any).fbq('init', META_PIXEL_ID);
      (window as any).fbq('track', 'PageView');
    } catch (e) {
      console.warn('Meta Pixel error:', e);
    }
  }, []);
  // Handle Add to Cart click
  const handleAddToCart = () => {
    try {
      if ((window as any).fbq) {
        ;
        (window as any).fbq('track', 'AddToCart', {
          content_name: 'Menopausia en Calma - Ebook',
          content_type: 'product',
          value: 6.39,
          currency: 'USD'
        });
      }
    } catch (e) {
      console.warn('Meta Pixel error:', e);
    }
    window.open('https://narelaspace.com/cart/47527062569180:1', '_blank');
  };
  const scrollToPrice = () => {
    const element = document.getElementById('precio-final');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <div className="min-h-screen bg-brand-cream font-sans text-brand-charcoal selection:bg-brand-teal selection:text-white overflow-x-hidden" data-id="element-9">
      {/* 1. HERO SECTION */}
      <section className="relative pt-8 pb-10 lg:pt-12 lg:pb-14 px-5 overflow-hidden" data-id="element-10">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-6 items-center" data-id="element-11">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="z-10" data-id="element-12">
            <motion.span variants={fadeInUp} className="inline-block px-3 py-1 mb-3 text-xs font-semibold tracking-wide text-brand-charcoal bg-brand-teal/20 rounded-full" data-id="element-13">
              Para mujeres de 40 a 60 años
            </motion.span>
            <motion.h1 variants={fadeInUp} className="text-3xl lg:text-4xl font-serif font-bold leading-tight mb-3 text-brand-dark" data-id="element-14">
              No es tu "edad": <br data-id="element-15" />
              <span className="text-brand-teal italic" data-id="element-16">
                es falta de un sistema.
              </span>
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-base lg:text-lg text-gray-600 mb-4 leading-relaxed max-w-lg" data-id="element-17">
              Sofocos, insomnio y barriga: un solo tablero para ordenar todo y
              recuperar el control de tu cuerpo.
            </motion.p>
            <motion.div variants={fadeInUp} data-id="element-18">
              <button onClick={scrollToPrice} className="w-full sm:w-auto px-6 py-3 bg-brand-coral hover:bg-[#D9966B] text-white font-bold rounded-lg shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-0.5 flex items-center justify-center gap-2" data-id="element-19">
                Quiero mi Tablero AM/PM
                <ArrowRight className="w-4 h-4" data-id="element-20" />
              </button>
            </motion.div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 40
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="relative z-10 flex justify-center lg:justify-end" data-id="element-21">
            <div className="relative w-full max-w-sm" data-id="element-22">
              <div className="absolute inset-0 bg-brand-teal/20 rounded-full blur-3xl transform translate-y-8 scale-90" data-id="element-23"></div>
              <img src={ebookImage} alt="Menopausia en Calma Ebook en Tablet" className="relative z-10 w-full drop-shadow-2xl rounded-xl" data-id="element-24" />
            </div>
          </motion.div>
        </div>
        <div className="absolute top-0 right-0 -mr-16 -mt-16 w-72 h-72 bg-brand-coral/10 rounded-full blur-3xl" data-id="element-25"></div>
      </section>

      {/* 2. EMOTIONAL OPENING */}
      <section className="py-8 px-5 bg-white" data-id="element-26">
        <div className="max-w-3xl mx-auto text-center" data-id="element-27">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} variants={staggerContainer} data-id="element-28">
            <motion.h2 variants={fadeInUp} className="text-xl lg:text-2xl font-serif italic text-gray-500 mb-5" data-id="element-29">
              Hay un momento específico que casi nadie dice en voz alta...
            </motion.h2>

            <motion.div variants={fadeInUp} className="space-y-3 text-base lg:text-lg leading-relaxed text-gray-700" data-id="element-30">
              <p data-id="element-31">
                Cuando estás en la cama, cansada… y aun así tu cuerpo no se
                apaga.
              </p>
              <p data-id="element-32">
                Te das vuelta. Miras la hora.{' '}
                <span className="font-bold text-brand-dark" data-id="element-33">
                  2:47am. 3:11am.
                </span>
              </p>
              <p data-id="element-34">
                Y ahí viene el pensamiento:{' '}
                <span className="italic" data-id="element-35">"¿Qué me está pasando?"</span>
              </p>
              <p data-id="element-36">
                No es solo el sueño. Es la sensación de que tu cuerpo ya no te
                obedece.
              </p>
              <p className="font-medium text-brand-teal text-lg py-1" data-id="element-37">
                Sofocos. Hinchazón. Irritabilidad.
              </p>
              <p data-id="element-38">
                Y el peor:{' '}
                <span className="font-bold text-brand-dark" data-id="element-39">
                  no reconocerte.
                </span>
              </p>
              <div className="pt-3" data-id="element-40">
                <p className="font-serif" data-id="element-41">
                  Este no es otro ebook para "informarte".
                </p>
                <p className="font-serif font-bold text-brand-coral" data-id="element-42">
                  Es un tablero simple para volver a tener control.
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* 3. PROBLEM IDENTIFICATION */}
      <section className="py-8 px-5 bg-brand-cream relative overflow-hidden" data-id="element-43">
        <div className="max-w-4xl mx-auto" data-id="element-44">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="bg-white rounded-xl p-5 lg:p-8 shadow-xl border border-brand-teal/10" data-id="element-45">
            <h2 className="text-xl lg:text-2xl font-serif font-bold mb-5 text-center text-brand-dark" data-id="element-46">
              Esto no es "tu culpa" <br data-id="element-47" />
              <span className="text-sm font-sans font-normal text-gray-500" data-id="element-48">
                (pero sí es tu responsabilidad recuperar el control)
              </span>
            </h2>

            <div className="grid md:grid-cols-2 gap-6 items-center" data-id="element-49">
              <div data-id="element-50">
                <p className="mb-3 text-gray-700 text-sm" data-id="element-51">Si te han dicho:</p>
                <ul className="space-y-1.5 mb-4" data-id="element-52">
                  {['Es normal, aguántate', 'Toma esto', 'Haz dieta', 'Haz ejercicio'].map((item, i) => <li key={i} className="flex items-center text-gray-600 bg-gray-50 p-2 rounded-lg text-sm" data-id="element-53">
                      <X className="w-4 h-4 text-red-400 mr-2 flex-shrink-0" data-id="element-54" />
                      {item}
                    </li>)}
                </ul>
                <p className="text-gray-700 font-medium text-sm" data-id="element-55">
                  Y tú… sigues igual (o peor). No es porque seas floja.
                </p>
              </div>

              <div className="bg-brand-teal/10 p-5 rounded-xl" data-id="element-56">
                <h3 className="text-base font-bold text-brand-dark mb-2" data-id="element-57">
                  El problema real es vivir así:
                </h3>
                <ul className="space-y-2" data-id="element-58">
                  {['Con sueño roto', 'Con calor que sube sin avisar', "Con barriga que aparece aunque no comas 'tanto'", 'Con humor que se te va de las manos'].map((item, i) => <li key={i} className="flex items-start" data-id="element-59">
                      <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand-coral mr-2 flex-shrink-0" data-id="element-60"></div>
                      <span className="text-gray-700 text-sm" data-id="element-61">{item}</span>
                    </li>)}
                </ul>
                <p className="mt-3 text-xs text-gray-500 italic" data-id="element-62">
                  Y encima… tener que seguir cumpliendo. Trabajo. Casa. Familia.
                  Todo.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 4. WHY OLD SOLUTIONS FAIL */}
      <section className="py-8 px-5 bg-brand-dark text-white" data-id="element-63">
        <div className="max-w-4xl mx-auto text-center" data-id="element-64">
          <motion.h2 initial={{
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-xl lg:text-2xl font-serif font-bold mb-6" data-id="element-65">
            Lo que la mayoría hace (y por qué falla)
          </motion.h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-6 relative" data-id="element-66">
            <div className="hidden md:block absolute top-1/2 left-0 w-full h-0.5 bg-gray-700 -z-0 transform -translate-y-1/2" data-id="element-67"></div>
            {[{
            step: '1',
            title: 'Prueba un té',
            desc: 'No pasa nada'
          }, {
            step: '2',
            title: 'Deja de comer X',
            desc: 'Lo aguanta 3 días'
          }, {
            step: '3',
            title: 'Compra algo',
            desc: 'No sabe si ayudó'
          }, {
            step: '4',
            title: 'Se cansa',
            desc: 'Vuelve el caos'
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 15
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.08
          }} className="bg-gray-800 p-3 rounded-lg relative z-10 border border-gray-700" data-id="element-68">
                <div className="w-6 h-6 bg-brand-coral rounded-full flex items-center justify-center font-bold text-white text-xs mx-auto mb-2" data-id="element-69">
                  {item.step}
                </div>
                <h3 className="font-bold text-xs mb-0.5" data-id="element-70">{item.title}</h3>
                <p className="text-[10px] text-gray-400" data-id="element-71">{item.desc}</p>
              </motion.div>)}
          </div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} className="bg-white/10 p-4 rounded-lg backdrop-blur-sm inline-block max-w-xl" data-id="element-72">
            <p className="text-sm" data-id="element-73">
              Falla por una sola razón: <br data-id="element-74" />
              <span className="text-brand-teal font-bold text-base block mt-1" data-id="element-75">
                No hay secuencia, no hay medición, no hay tablero.
              </span>
            </p>
          </motion.div>
        </div>
      </section>

      {/* 5. THE METHOD */}
      <section className="py-10 px-5 bg-white" data-id="element-76">
        <div className="max-w-5xl mx-auto" data-id="element-77">
          <div className="text-center mb-6" data-id="element-78">
            <span className="text-brand-coral font-bold tracking-wider uppercase text-xs" data-id="element-79">
              Presentando
            </span>
            <h2 className="text-2xl lg:text-3xl font-serif font-bold text-brand-dark mt-1 mb-2" data-id="element-80">
              Menopausia en Calma
            </h2>
            <p className="text-sm text-gray-600 max-w-xl mx-auto" data-id="element-81">
              Una guía práctica de 21 días. No es "más información". Es una
              solución diaria que funciona en la vida real.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-4" data-id="element-82">
            {[{
            icon: <Sun className="w-6 h-6 text-brand-coral" data-id="element-83" />,
            title: 'Tablero AM/PM',
            desc: 'Rutina de mañana para estabilizar energía + rutina de noche para apagar el sistema.',
            color: 'bg-orange-50'
          }, {
            icon: <Utensils className="w-6 h-6 text-brand-teal" data-id="element-84" />,
            title: 'Plato Anti-Sofoco',
            desc: 'Plantilla simple de comidas para regular sin dietas extremas ni pasar hambre.',
            color: 'bg-teal-50'
          }, {
            icon: <ClipboardCheck className="w-6 h-6 text-purple-500" data-id="element-85" />,
            title: 'Tracker de Síntomas',
            desc: '5 minutos al día para descubrir tus detonantes reales en 7–14 días.',
            color: 'bg-purple-50'
          }].map((card, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className={`${card.color} p-5 rounded-xl border border-gray-100`} data-id="element-86">
                <div className="bg-white w-12 h-12 rounded-full flex items-center justify-center shadow-sm mb-3" data-id="element-87">
                  {card.icon}
                </div>
                <h3 className="text-lg font-serif font-bold text-brand-dark mb-2" data-id="element-88">
                  {card.title}
                </h3>
                <p className="text-gray-700 text-sm leading-relaxed" data-id="element-89">
                  {card.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* 6. BENEFITS */}
      <section className="py-8 px-5 bg-brand-cream" data-id="element-90">
        <div className="max-w-4xl mx-auto" data-id="element-91">
          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} className="bg-white rounded-xl p-5 lg:p-8 shadow-xl relative overflow-hidden" data-id="element-92">
            <div className="absolute top-0 right-0 w-40 h-40 bg-brand-teal/10 rounded-full blur-3xl -mr-10 -mt-10" data-id="element-93"></div>
            <h2 className="text-xl font-serif font-bold text-center mb-5 relative z-10" data-id="element-94">
              Lo que vas a notar{' '}
              <span className="text-sm font-sans font-normal text-gray-500" data-id="element-95">
                (sin promesas mágicas)
              </span>
            </h2>

            <div className="grid sm:grid-cols-2 gap-3 relative z-10" data-id="element-96">
              {["Menos noches 'rotas'", 'Sofocos que bajan intensidad', 'Hinchazón que deja de ser diaria', "Sensación de 'otra vez soy yo'"].map((benefit, i) => <motion.div key={i} initial={{
              opacity: 0,
              x: -15
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.08
            }} className="flex items-center p-2.5 bg-brand-cream/50 rounded-lg" data-id="element-97">
                  <div className="w-6 h-6 bg-brand-teal rounded-full flex items-center justify-center mr-2 flex-shrink-0" data-id="element-98">
                    <Check className="w-3.5 h-3.5 text-white" data-id="element-99" />
                  </div>
                  <span className="font-medium text-brand-dark text-sm" data-id="element-100">
                    {benefit}
                  </span>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* 7. WHO IS THIS FOR */}
      <section className="py-8 px-5 bg-white" data-id="element-101">
        <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-5" data-id="element-102">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="space-y-3" data-id="element-103">
            <h3 className="text-lg font-serif font-bold text-brand-teal flex items-center gap-2" data-id="element-104">
              <Check className="w-5 h-5" data-id="element-105" />
              ¿Para quién ES?
            </h3>
            <p className="text-gray-600 text-sm" data-id="element-106">
              Para mujeres de 40 a 60 años que:
            </p>
            <ul className="space-y-2" data-id="element-107">
              <li className="flex items-start gap-2" data-id="element-108">
                <div className="w-1.5 h-1.5 bg-brand-teal rounded-full mt-1.5" data-id="element-109"></div>
                <span className="text-sm" data-id="element-110">
                  Ya probaron cosas sueltas y no funcionaron.
                </span>
              </li>
              <li className="flex items-start gap-2" data-id="element-111">
                <div className="w-1.5 h-1.5 bg-brand-teal rounded-full mt-1.5" data-id="element-112"></div>
                <span className="text-sm" data-id="element-113">
                  Están cansadas de explicaciones médicas complejas.
                </span>
              </li>
              <li className="flex items-start gap-2" data-id="element-114">
                <div className="w-1.5 h-1.5 bg-brand-teal rounded-full mt-1.5" data-id="element-115"></div>
                <span className="text-sm" data-id="element-116">
                  Quieren un plan que se pueda sostener en el tiempo.
                </span>
              </li>
            </ul>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="space-y-3 bg-gray-50 p-5 rounded-xl" data-id="element-117">
            <h3 className="text-lg font-serif font-bold text-gray-400 flex items-center gap-2" data-id="element-118">
              <X className="w-5 h-5" data-id="element-119" />
              ¿Para quién NO es?
            </h3>
            <ul className="space-y-2 text-gray-500" data-id="element-120">
              <li className="flex items-start gap-2" data-id="element-121">
                <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5" data-id="element-122"></div>
                <span className="text-sm" data-id="element-123">
                  Si buscas tratamiento médico personalizado.
                </span>
              </li>
              <li className="flex items-start gap-2" data-id="element-124">
                <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5" data-id="element-125"></div>
                <span className="text-sm" data-id="element-126">
                  Si buscas bajar peso extremo rápido.
                </span>
              </li>
              <li className="flex items-start gap-2" data-id="element-127">
                <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5" data-id="element-128"></div>
                <span className="text-sm" data-id="element-129">
                  Si no quieres hacer rutinas cortas diarias.
                </span>
              </li>
            </ul>
          </motion.div>
        </div>
      </section>

      {/* 8. WHAT'S INCLUDED */}
      <section className="py-8 px-5 bg-brand-cream" data-id="element-130">
        <div className="max-w-4xl mx-auto" data-id="element-131">
          <h2 className="text-xl font-serif font-bold text-center mb-5 text-brand-dark" data-id="element-132">
            Qué incluye exactamente
          </h2>

          <div className="grid sm:grid-cols-2 gap-3" data-id="element-133">
            {[{
            icon: <BookOpen className="w-5 h-5 text-brand-coral" data-id="element-134" />,
            title: 'Ebook Completo',
            desc: 'Estructura paso a paso'
          }, {
            icon: <Clock className="w-5 h-5 text-brand-teal" data-id="element-135" />,
            title: 'Plan 21 días',
            desc: 'Guía de ejecución diaria'
          }, {
            icon: <ShieldCheck className="w-5 h-5 text-blue-500" data-id="element-136" />,
            title: 'Protocolos de emergencia',
            desc: 'Sofoco en público, despertar 3am'
          }, {
            icon: <ClipboardCheck className="w-5 h-5 text-purple-500" data-id="element-137" />,
            title: 'Checklists y trackers',
            desc: 'Listos para imprimir/usar'
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 15
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.08
          }} className="bg-white p-3 rounded-lg shadow-sm flex items-start gap-3" data-id="element-138">
                <div className="bg-gray-50 p-2 rounded-lg" data-id="element-139">{item.icon}</div>
                <div data-id="element-140">
                  <h4 className="font-bold text-brand-dark text-sm" data-id="element-141">
                    {item.title}
                  </h4>
                  <p className="text-gray-600 text-xs" data-id="element-142">{item.desc}</p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* 9. FINAL CTA */}
      <section id="precio-final" className="py-10 px-5 bg-brand-teal relative overflow-hidden" data-id="element-143">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10" data-id="element-144"></div>
        <div className="max-w-3xl mx-auto text-center relative z-10" data-id="element-145">
          <motion.h2 initial={{
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-xl lg:text-3xl font-serif font-bold text-white mb-4 leading-tight" data-id="element-146">
            Si hoy sientes que tu cuerpo "manda"... <br data-id="element-147" />
            no necesitas otro consejo.
          </motion.h2>

          <motion.p initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.15
        }} className="text-base text-brand-cream mb-6 font-medium" data-id="element-148">
            Necesitas un tablero. Menopausia en Calma te lo da.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="bg-white p-5 rounded-xl shadow-2xl inline-block w-full max-w-xs" data-id="element-149">
            <div className="text-2xl font-bold text-brand-dark mb-1" data-id="element-150">
              US$6.39
            </div>
            <div className="flex items-center justify-center gap-1 text-gray-500 mb-4 text-xs" data-id="element-151">
              <Download className="w-3.5 h-3.5" data-id="element-152" />
              <span data-id="element-153">Descarga inmediata en PDF</span>
            </div>

            <button onClick={handleAddToCart} className="w-full py-3 bg-brand-coral hover:bg-[#D9966B] text-white font-bold rounded-lg shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-0.5 flex items-center justify-center gap-2" data-id="element-154">
              Quiero mi Tablero AM/PM
              <ArrowRight className="w-4 h-4" data-id="element-155" />
            </button>

            <p className="mt-2 text-[10px] text-gray-400" data-id="element-156">
              Compra segura • Recibes el ebook al instante
            </p>
          </motion.div>
        </div>
      </section>

      {/* 10. FOOTER */}
      <footer className="py-6 px-5 bg-brand-dark text-gray-400 text-xs text-center" data-id="element-157">
        <div className="max-w-6xl mx-auto" data-id="element-158">
          <p className="mb-2" data-id="element-159">
            &copy; {new Date().getFullYear()} Menopausia en Calma. Todos los
            derechos reservados.
          </p>
          <div className="flex justify-center gap-4" data-id="element-160">
            <button onClick={() => setTermsOpen(true)} className="hover:text-white transition-colors" data-id="element-161">
              Términos y Condiciones
            </button>
            <button onClick={() => setPrivacyOpen(true)} className="hover:text-white transition-colors" data-id="element-162">
              Política de Privacidad
            </button>
          </div>
        </div>
      </footer>

      {/* Privacy Modal */}
      <Modal isOpen={privacyOpen} onClose={() => setPrivacyOpen(false)} title="Política de Privacidad" data-id="element-163">
        <div className="space-y-3" data-id="element-164">
          <h3 className="font-bold text-brand-dark" data-id="element-165">
            1. Información que Recopilamos
          </h3>
          <p data-id="element-166">
            Recopilamos información personal que usted nos proporciona
            directamente, como su nombre y dirección de correo electrónico
            cuando realiza una compra.
          </p>
          <h3 className="font-bold text-brand-dark" data-id="element-167">
            2. Uso de la Información
          </h3>
          <p data-id="element-168">
            Utilizamos la información recopilada para procesar sus pedidos,
            enviar actualizaciones sobre su compra, y mejorar nuestros productos
            y servicios.
          </p>
          <h3 className="font-bold text-brand-dark" data-id="element-169">3. Protección de Datos</h3>
          <p data-id="element-170">
            Implementamos medidas de seguridad técnicas y organizativas para
            proteger su información personal contra acceso no autorizado,
            pérdida o alteración.
          </p>
          <h3 className="font-bold text-brand-dark" data-id="element-171">
            4. Compartir Información
          </h3>
          <p data-id="element-172">
            No vendemos, intercambiamos ni transferimos su información personal
            a terceros sin su consentimiento, excepto cuando sea necesario para
            procesar su pedido.
          </p>
          <h3 className="font-bold text-brand-dark" data-id="element-173">5. Sus Derechos</h3>
          <p data-id="element-174">
            Usted tiene derecho a acceder, rectificar o eliminar su información
            personal. También puede oponerse al procesamiento de sus datos en
            cualquier momento.
          </p>
          <p className="text-xs text-gray-500 pt-2" data-id="element-175">
            Última actualización: Febrero 2026
          </p>
        </div>
      </Modal>

      {/* Terms Modal */}
      <Modal isOpen={termsOpen} onClose={() => setTermsOpen(false)} title="Términos y Condiciones" data-id="element-176">
        <div className="space-y-3" data-id="element-177">
          <h3 className="font-bold text-brand-dark" data-id="element-178">
            1. Aceptación de Términos
          </h3>
          <p data-id="element-179">
            Al acceder y utilizar este sitio web y realizar una compra, usted
            acepta estar sujeto a estos términos y condiciones de uso.
          </p>
          <h3 className="font-bold text-brand-dark" data-id="element-180">2. Producto Digital</h3>
          <p data-id="element-181">
            "Menopausia en Calma" es un producto digital (ebook en PDF) que se
            entrega electrónicamente. Una vez completada la compra, recibirá el
            archivo para descarga inmediata.
          </p>
          <h3 className="font-bold text-brand-dark" data-id="element-182">3. Uso Personal</h3>
          <p data-id="element-183">
            El contenido adquirido es para uso personal únicamente. Queda
            prohibida la reproducción, distribución o reventa del material sin
            autorización expresa.
          </p>
          <h3 className="font-bold text-brand-dark" data-id="element-184">
            4. Descargo de Responsabilidad
          </h3>
          <p data-id="element-185">
            El contenido de este ebook es informativo y educativo. No sustituye
            el consejo médico profesional. Consulte siempre con un profesional
            de la salud antes de realizar cambios en su rutina.
          </p>
          <h3 className="font-bold text-brand-dark" data-id="element-186">
            5. Propiedad Intelectual
          </h3>
          <p data-id="element-187">
            Todo el contenido, incluyendo textos, gráficos y diseños, está
            protegido por derechos de autor y es propiedad de Menopausia en
            Calma.
          </p>
          <p className="text-xs text-gray-500 pt-2" data-id="element-188">
            Última actualización: Febrero 2026
          </p>
        </div>
      </Modal>
    </div>;
}